import type { EvaluationDraft, PublicConfig } from '../../types';
import type { DraftErrors } from '../../hooks/useEvaluationDraft';
import { EvaluationModeTabs } from './EvaluationModeTabs';
import { ArticleEditor } from './ArticleEditor';

interface Props {
  value: EvaluationDraft;
  errors: DraftErrors;
  config: PublicConfig;
  submitting: boolean;
  onChange: (value: EvaluationDraft) => void;
  onNormalize: () => void;
  onSubmit: () => void;
}
export function EvaluationForm({
  value,
  errors,
  config,
  submitting,
  onChange,
  onNormalize,
  onSubmit
}: Props) {
  const fillExample = () =>
    onChange({
      ...value,
      task_name: '品牌文章 GEO 评测',
      keywords_text: 'AI 搜索优化, GEO, 品牌曝光',
      article_a: {
        title: '如何提升品牌在 AI 搜索中的可见度',
        body: '生成式 AI 正在改变用户获取信息的方式。企业需要明确品牌实体、补充可信来源，并通过清晰的小标题和结论前置，让内容更容易被理解和引用。\n\n建议从内容结构、证据来源、用户意图和品牌一致性四个方面持续优化。'
      },
      article_b:
        value.mode === 'compare'
          ? {
              title: '品牌内容优化的另一种表达',
              body: '面对 AI 搜索场景，品牌内容不仅要覆盖关键词，还要提供可以验证的事实、清晰的步骤以及稳定的企业和产品名称。通过持续更新内容，可以提升被 AI 收录与引用的机会。'
            }
          : value.article_b
    });
  return (
    <form
      className="tool"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="tool-head">
        <div>
          <h2>创建文章评测</h2>
          <p>提交后由独立评测服务生成报告，正文不会写入网址或埋点。</p>
        </div>
        <EvaluationModeTabs value={value.mode} onChange={(mode) => onChange({ ...value, mode })} />
      </div>
      <div className="tool-body">
        <div className="fields">
          <div>
            <label>
              任务名
              <input
                value={value.task_name}
                maxLength={200}
                aria-invalid={!!errors.task_name}
                onChange={(e) => onChange({ ...value, task_name: e.target.value })}
                placeholder="例如：7 月品牌文章评测"
              />
            </label>
            {errors.task_name && <span className="field-error">{errors.task_name}</span>}
          </div>
          <div>
            <label>
              核心关键词
              <input
                value={value.keywords_text}
                maxLength={config.keywords_max_chars + 20}
                aria-invalid={!!errors.keywords_text}
                onBlur={onNormalize}
                onChange={(e) => onChange({ ...value, keywords_text: e.target.value })}
                placeholder="逗号、分号或换行分隔"
              />
            </label>
            {errors.keywords_text && <span className="field-error">{errors.keywords_text}</span>}
          </div>
        </div>
        <div className={`article-grid ${value.mode === 'compare' ? 'compare-mode' : ''}`}>
          <ArticleEditor
            slot="A"
            value={value.article_a}
            maxChars={config.article_max_chars}
            titleError={errors.article_a_title}
            bodyError={errors.article_a_body}
            onChange={(article_a) => onChange({ ...value, article_a })}
          />
          {value.mode === 'compare' && (
            <ArticleEditor
              slot="B"
              value={value.article_b}
              maxChars={config.article_max_chars}
              titleError={errors.article_b_title}
              bodyError={errors.article_b_body}
              onChange={(article_b) => onChange({ ...value, article_b })}
            />
          )}
        </div>
        <div className="tool-foot">
          <div className="quota">
            <span className="pill">每日 {config.evaluation_units_per_day} 额度</span>
            <button type="button" className="link-button" onClick={fillExample}>
              填充示例
            </button>
          </div>
          <div className="submit-block">
            <button type="submit" className="btn green large" disabled={submitting}>
              {submitting
                ? '正在提交…'
                : `生成评测报告 · 消耗 ${value.mode === 'compare' ? config.compare_mode_units : config.single_mode_units} 额度`}
            </button>
            <p className="submit-note">
              提交即表示你确认拥有文章处理权限。敏感正文不会进入产品事件。
            </p>
          </div>
        </div>
      </div>
    </form>
  );
}
